"use client";

import { buttonVariants } from "@/components/ui/button";
import {
  isFeatureFlagBenefitNotificationEnable,
  isFeatureFlagStressCheckEnabled,
} from "@/env/config";
import { cn } from "@/lib/utils";
import {
  type AdminAbilityInstance,
  buildAdminAbility,
  checkAdminAbility,
} from "@/server/models/admin-staff/admin-ability";
import type { AdminAbility } from "@/server/models/admin-staff/admin-staff-constants";
import { pagesPath } from "@/utils/pages-path";
import Link from "next/link";
import { useMemo } from "react";

type MenuItem = {
  /**
   * このメニューを見せる条件
   *
   * メニューは「押したら開ける画面」だけを見せたいので、**リンク先が要求する
   * 参照権限**を並べる。リンク先が複数の領域を持つハブ画面（申請管理・通知管理）は
   * どれか 1 つでも開けるなら見せるため、複数並べて OR で判定する。
   */
  requiredAbilities: ReadonlyArray<AdminAbility>;
  label: string;
  href: string;
};

const MENU_CATEGORIES: Record<string, MenuItem[]> = {
  // ユーザー管理カテゴリのメニュー
  userManagement: [
    {
      requiredAbilities: [{ subject: "Member", action: "read" }],
      label: "加入者管理",
      href: pagesPath.admin.list.members.$url().path,
    },
    {
      requiredAbilities: [{ subject: "Office", action: "read" }],
      label: "事業所管理",
      href: pagesPath.admin.list.offices.$url().path,
    },
    {
      requiredAbilities: [{ subject: "Staff", action: "read" }],
      label: "健保スタッフ管理",
      href: pagesPath.admin.list.admins.$url().path,
    },
    {
      requiredAbilities: [{ subject: "Account", action: "read" }],
      label: "アカウント管理",
      href: pagesPath.admin.list.accounts.$url().path,
    },
    {
      requiredAbilities: [{ subject: "OfficeBankAccount", action: "read" }],
      label: "口座情報管理",
      href: pagesPath.admin.list.bank_accounts.offices.$url().path,
    },
  ],

  // 健康診断カテゴリのメニュー
  healthCheckup: [
    {
      requiredAbilities: [{ subject: "HealthCheckupFacility", action: "read" }],
      label: "健診マスタ",
      href: pagesPath.admin.list.medical_checkups.reference_data.facilities.$url()
        .path,
    },
    {
      requiredAbilities: [{ subject: "MedicalCheckup", action: "read" }],
      label: "健診予約",
      href: pagesPath.admin.list.medical_checkups.$url().path,
    },
    {
      requiredAbilities: [{ subject: "MedicalCheckup", action: "read" }],
      label: "健診結果・報告",
      href: pagesPath.admin.list.medical_checkups.results.$url().path,
    },
    {
      requiredAbilities: [{ subject: "MedicalCheckup", action: "read" }],
      label: "特定保健指導",
      href: pagesPath.admin.list.medical_checkups.tokutei_hokenshido.$url()
        .path,
    },
  ],

  // ストレスチェックカテゴリのメニュー
  stressCheck: isFeatureFlagStressCheckEnabled()
    ? [
        {
          requiredAbilities: [{ subject: "StressCheck", action: "read" }],
          label: "ストレスチェック実施状況",
          href: pagesPath.admin.list.stress_checks.$url().path,
        } satisfies MenuItem,
      ]
    : [],

  // 通知・システムカテゴリのメニュー
  notificationSystem: [
    {
      // アクティビティとバウンスは別の ability で守られたタブなので、
      // どちらか片方でも開けるならメニューを見せる
      requiredAbilities: [
        { subject: "EmailActivity", action: "read" },
        { subject: "EmailBounce", action: "read" },
      ],
      label: "Eメール管理",
      href: pagesPath.admin.list.emails.$url().path,
    },
    {
      // リンク先（通知管理のハブ画面）の requiredAnyAbility と揃える。
      // メール通知管理はアドレス帳一覧へリダイレクトするので AddressBook:read
      requiredAbilities: [
        { subject: "AddressBook", action: "read" },
        { subject: "CustomNotification", action: "read" },
        { subject: "EmployerEmailNotification", action: "read" },
        { subject: "SystemNotification", action: "read" },
      ],
      label: "通知管理",
      href: pagesPath.admin.list.notifications.$url().path,
    },
    {
      requiredAbilities: [
        { subject: "CharacterConversionRule", action: "read" },
      ],
      label: "加入者の認証時のマイナアプリの文字変換ルール管理",
      href: pagesPath.admin.list.character_conversion_rules.$url().path,
    },
  ],

  // 申請・通知書
  applicationAndNotificationPapers: [
    {
      requiredAbilities: [
        { subject: "Fuufukyoudoufuyoukakunin", action: "read" },
        { subject: "Hifuyoushashikakukakunin", action: "read" },
        { subject: "HihuyousyaIdouTodoke", action: "read" },
        { subject: "Ryoyohitatekae", action: "read" },
        { subject: "Shussanteatekin", action: "read" },
      ],
      label: "申請管理",
      href: pagesPath.admin.list.applications.$url().path,
    },
    ...(isFeatureFlagBenefitNotificationEnable()
      ? [
          {
            requiredAbilities: [
              { subject: "NotificationPaper", action: "read" },
            ],
            label: "通知書管理",
            href: pagesPath.admin.list.notification_papers.$url().path,
          } satisfies MenuItem,
        ]
      : []),
  ],
};

// カテゴリ別にメニューを描画するコンポーネント
const MenuCategory = ({
  title,
  menus,
  ability,
}: {
  title: string;
  menus: MenuItem[];
  ability: AdminAbilityInstance;
}) => {
  const visibleMenus = menus.filter((menu) =>
    // リンク先のどれか 1 つでも開けるなら見せる
    menu.requiredAbilities.some(({ subject, action }) =>
      checkAdminAbility(ability, subject, action),
    ),
  );

  if (visibleMenus.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 className="mb-4 border-b border-monotone-3 pb-2 text-lg font-semibold text-monotone-9">
        {title}
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {visibleMenus.map((menu) => (
          <Link
            key={menu.label}
            className={cn(
              buttonVariants({ variant: "primary", size: "lg" }),
              "h-16 w-full",
            )}
            href={menu.href}
          >
            {menu.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const AdminRootPage = ({
  userInfo,
}: {
  userInfo: {
    abilities: ReadonlyArray<AdminAbility>;
  };
}) => {
  const ability = useMemo(
    () => buildAdminAbility(userInfo.abilities),
    [userInfo.abilities],
  );

  return (
    <div className="p-10">
      <div className="space-y-8">
        <MenuCategory
          title="ユーザー管理"
          menus={MENU_CATEGORIES.userManagement}
          ability={ability}
        />
        <MenuCategory
          title="健康診断"
          menus={MENU_CATEGORIES.healthCheckup}
          ability={ability}
        />
        <MenuCategory
          title="ストレスチェック"
          menus={MENU_CATEGORIES.stressCheck}
          ability={ability}
        />
        <MenuCategory
          title="申請・通知書"
          menus={MENU_CATEGORIES.applicationAndNotificationPapers}
          ability={ability}
        />
        <MenuCategory
          title="通知・システム"
          menus={MENU_CATEGORIES.notificationSystem}
          ability={ability}
        />
      </div>
    </div>
  );
};
